import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { httpPost, httpPostFile, useHttpGets } from "../../services/httpService";
import { ApiEndPoints } from "../../utils/endpoints";
import { IFileDetails, IAPIResponse } from "../../interface/files";
import { Status } from "../../utils/constants";

interface IAttachmentState {
  fetchAttachmentFiles: {
    data: IFileDetails[];
    error: string;
    status: string;
  };
  deleteAttachmentsResponse: IAPIResponse;
  uploadAttachmentResponse: IAPIResponse;
}

interface IDeleteFileParams {
  FileName: string;
  WatchlistID: string;
}

interface IFetchFileParams {
  WatchlistID: string;
  isAttachementData: boolean;
}

interface IUploadFile {
  file: File;
  memberPropertyId: string;
  isAttachment: number;
}

const initialState: IAttachmentState = {
  fetchAttachmentFiles: {
    data: {} as IFileDetails[],
    error: "",
    status: "",
  },
  uploadAttachmentResponse: {
    data: {},
    error: "",
    status: "",
  },
  deleteAttachmentsResponse: {
    data: {},
    error: "",
    status: "",
  },
};

export const fetchAttachmentFiles = createAsyncThunk(
  "attachments/fetchAttachmentFiles",
  async (params: IFetchFileParams, { rejectWithValue }) => {
    try {
      const result = await useHttpGets(`${ApiEndPoints.files}/AllFiles/${params.WatchlistID}`);
      if (result.status === 200) {
        return result.data?.Attachements?.Files || [];
      } else {
        return rejectWithValue(result.error);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

export const uploadAttachmentFile = createAsyncThunk(
  "attachments/uploadAttachmentFile",
  async (file: IUploadFile, { rejectWithValue }) => {
    try {
      const result = await httpPostFile(`${ApiEndPoints.files}Uploads/HtmlClient/`, file);
      if (result.status === 200) {
        return file.file;
      } else {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(result.error || "", "text/xml");
        const message = xmlDoc.documentElement.textContent;
        return rejectWithValue(message);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

export const deleteAttachmentFile = createAsyncThunk(
  "attachments/deleteAttachmentFile",
  async (files: IDeleteFileParams, { rejectWithValue }) => {
    const fileNames = [files.FileName];
    try {
      const response = await httpPost(`${ApiEndPoints.files}DeleteFiles`, {
        MemberPropertyId: `${files.WatchlistID}`,
        FileNames: fileNames,
      });
      if (response.error == null) {
        return files.FileName;
      } else {
        return rejectWithValue(response.error);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

const attachmentSlice = createSlice({
  name: "attachments",
  initialState,
  reducers: {
    resetFileUploadState: (state) => {
      state.uploadAttachmentResponse = { data: {}, error: "", status: "" };
    },
    resteDeleteFileState: (state) => {
      state.deleteAttachmentsResponse = { data: {}, error: "", status: "" };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttachmentFiles.fulfilled, (state, action) => {
        state.fetchAttachmentFiles.data = action.payload;
        state.fetchAttachmentFiles.status = Status.SUCCESS;
        state.fetchAttachmentFiles.error = "";
      })
      .addCase(fetchAttachmentFiles.pending, (state) => {
        state.fetchAttachmentFiles.status = Status.LOADING;
        state.fetchAttachmentFiles.error = "";
      })
      .addCase(fetchAttachmentFiles.rejected, (state, action) => {
        state.fetchAttachmentFiles.status = Status.FAILED;
        state.fetchAttachmentFiles.error = action.payload as string;
      })
      .addCase(uploadAttachmentFile.fulfilled, (state, action) => {
        state.uploadAttachmentResponse.data = action.payload;
        state.uploadAttachmentResponse.status = Status.SUCCESS;
        state.uploadAttachmentResponse.error = "";
      })
      .addCase(uploadAttachmentFile.pending, (state) => {
        state.uploadAttachmentResponse.status = Status.LOADING;
        state.uploadAttachmentResponse.error = "";
      })
      .addCase(uploadAttachmentFile.rejected, (state, action) => {
        state.uploadAttachmentResponse.status = Status.FAILED;
        state.uploadAttachmentResponse.error = action.payload as string;
      })
      .addCase(deleteAttachmentFile.fulfilled, (state, action) => {
        state.deleteAttachmentsResponse.data = action.payload;
        state.deleteAttachmentsResponse.status = Status.SUCCESS;
        state.deleteAttachmentsResponse.error = "";
      })
      .addCase(deleteAttachmentFile.pending, (state) => {
        state.deleteAttachmentsResponse.status = Status.LOADING;
        state.deleteAttachmentsResponse.error = "";
      })
      .addCase(deleteAttachmentFile.rejected, (state, action) => {
        state.deleteAttachmentsResponse.status = Status.FAILED;
        state.deleteAttachmentsResponse.error = action.payload as string;
      });
  },
});

export const { resteDeleteFileState, resetFileUploadState } = attachmentSlice.actions;

export default attachmentSlice.reducer;
